/**
 * packages/ui/src/icons/index.js
 * Public barrel export for Homy's Centralized Themeable Bespoke SVG Icon System.
 */

import { iconRegistry } from "./registry.js";
import { registerDefaultBadges } from "./badges/index.jsx";

// Domain glyph sets across 4 variants
import { duotoneDomainGlyphs } from "./variants/duotone/DomainIcons.jsx";
import { lineDomainGlyphs } from "./variants/line/DomainIcons.jsx";
import { glowDomainGlyphs } from "./variants/glow/DomainIcons.jsx";
import { solidDomainGlyphs } from "./variants/solid/DomainIcons.jsx";

// Action glyph sets across 4 variants
import { duotoneActionGlyphs } from "./variants/duotone/ActionIcons.jsx";
import { lineActionGlyphs } from "./variants/line/ActionIcons.jsx";
import { glowActionGlyphs } from "./variants/glow/ActionIcons.jsx";
import { solidActionGlyphs } from "./variants/solid/ActionIcons.jsx";

// View mode glyph sets across 4 variants
import { duotoneViewGlyphs } from "./variants/duotone/ViewIcons.jsx";
import { lineViewGlyphs } from "./variants/line/ViewIcons.jsx";
import { glowViewGlyphs } from "./variants/glow/ViewIcons.jsx";
import { solidViewGlyphs } from "./variants/solid/ViewIcons.jsx";

let _registryInitialized = false;

// --- Lazy registration of all core glyphs into the runtime registry ---
export function initializeRegistry() {
  if (_registryInitialized) return;
  _registryInitialized = true;
  iconRegistry._isInitialized = true;

  // 1. Action modifier badges
  registerDefaultBadges(iconRegistry);

  // 2. 21 core domain modules
  for (const name of Object.keys(duotoneDomainGlyphs)) {
    iconRegistry.register(name, {
      "spatial-bento-duotone": duotoneDomainGlyphs[name],
      "spatial-bento-line": lineDomainGlyphs[name],
      "spatial-bento-glow": glowDomainGlyphs[name],
      "obsidian-solid": solidDomainGlyphs[name],
    });
  }

  // 3. Action icons
  for (const name of Object.keys(duotoneActionGlyphs)) {
    iconRegistry.register(name, {
      "spatial-bento-duotone": duotoneActionGlyphs[name],
      "spatial-bento-line": lineActionGlyphs[name],
      "spatial-bento-glow": glowActionGlyphs[name],
      "obsidian-solid": solidActionGlyphs[name],
    });
  }

  // 4. View mode icons
  for (const name of Object.keys(duotoneViewGlyphs)) {
    iconRegistry.register(name, {
      "spatial-bento-duotone": duotoneViewGlyphs[name],
      "spatial-bento-line": lineViewGlyphs[name],
      "spatial-bento-glow": glowViewGlyphs[name],
      "obsidian-solid": solidViewGlyphs[name],
    });
  }
}

// Decouple eager evaluation: wire lazy initializer to populate glyphs on demand
iconRegistry.setInitializer(initializeRegistry);

// Core components & registry
export { HomyIcon } from "./HomyIcon.jsx";
export { CompositeActionIcon } from "./CompositeActionIcon.jsx";
export { IconRegistry, iconRegistry } from "./registry.js";

// Theme Context & Provider
export {
  IconThemeContext,
  IconThemeProvider,
  useIconTheme,
  THEME_STORAGE_KEY,
} from "./IconThemeContext.jsx";

// Domain taxonomy & metadata
export {
  domainMap,
  DOMAINS,
  getDomainConfig,
  getDomainColor,
  getDomainHex,
} from "./domainMap.js";

// Action modifier badges
export * from "./badges/index.jsx";

// Lucide compatibility adapters & composite factories
export {
  getDomainIcon,
  createLucideAdapter,
  createCompositeIcon,
  AddPropertyIcon,
  NewMaintenanceIcon,
  CreateTaskIcon,
  NewSupportTicketIcon,
  ScheduleVisitIcon,
  NewCalendarEventIcon,
  DraftLeaseIcon,
  CreateRentalIcon,
  RecordPaymentIcon,
  CreateListingIcon,
  AddVendorIcon,
  NewProjectIcon,
  AddMemberIcon,
  AddOrganizationIcon,
  CreateTemplateIcon,
  CreateApplicationIcon,
  CreateDatabaseIcon,
  CreateMarketplaceIcon,
  UpgradeMembershipIcon,
  NewCommentIcon,
  SendInboxMessageIcon,
  AddContactIcon,
} from "./adapters/lucideAdapter.jsx";

// Direct tree-shakable glyph exports
export * as DuotoneIcons from "./variants/duotone/DomainIcons.jsx";
export * as LineIcons from "./variants/line/DomainIcons.jsx";
export * as GlowIcons from "./variants/glow/DomainIcons.jsx";
export * as SolidIcons from "./variants/solid/DomainIcons.jsx";
export * from "./variants/duotone/ActionIcons.jsx";
export * from "./variants/duotone/ViewIcons.jsx";

// 20 Preset Avatars & UserAvatar
export * from "./avatars/presetAvatars.jsx";
export * from "./avatars/UserAvatar.jsx";

